/**
 * Payouts: the two queues that stand between a player and their money — the
 * identity checks, and the withdrawal requests themselves.
 *
 * They live on one screen because they are one decision in practice: an operator
 * opens the tab because somebody wants paying, and the first question is always
 * whether that person has been verified. A banner at the top says whether
 * cash-out is even open, so a silent, empty queue is never mistaken for "nobody
 * has asked" when the real answer is "the switch in Settings is off".
 */
import { el, clear, toast } from '../../dom';
import {
  adminApi,
  type AdminKycUser,
  type AdminSettings,
} from '../../game-api';
import {
  adminListWithdrawals,
  adminApproveWithdrawal,
  adminRejectWithdrawal,
} from '../../../core/payments/payments-api';
import { fmtDateTime } from './format';
import {
  apiMessage, btn, confirmDialog, emptyBox, errorBox, input, labelled, modal,
  panel, pill, spinner, table, toastError,
} from './ui';

interface CoreAdminWithdrawal {
  id: string;
  userId: string;
  email: string;
  amountCents: number;
  feeCents: number;
  netCents: number;
  currency: string;
  status: 'PENDING' | 'PROCESSING' | 'APPROVED' | 'REJECTED' | 'FAILED';
  payoutMethod: string | null;
  reason: string | null;
  createdAt: string;
  settledAt: string | null;
  invoiceNumber?: string | null;
  user?: { email: string; username: string | null };
}

const eur = (cents: number) => `€${(cents / 100).toFixed(2)}`;

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  APPROVED: 'ok', PENDING: 'warn', PROCESSING: 'warn', REJECTED: 'bad', FAILED: 'bad', NONE: 'mute',
};

export function renderPayouts(host: HTMLElement): () => void {
  let cancelled = false;

  const banner = el('div', 'min-h-0');
  const withdrawalsHost = el('div', 'min-h-[80px]');
  const kycHost = el('div', 'min-h-[80px]');

  host.appendChild(el('div', 'flex flex-col gap-4', {},
    banner,
    panel('Withdrawal requests', withdrawalsHost),
    panel('Identity verification', kycHost)));

  function renderBanner(s: AdminSettings | null) {
    clear(banner);
    if (!s) return;
    if (s.withdrawalEnabled) {
      banner.appendChild(el('div',
        'game-font rounded-lg border-2 border-[#4a6a3a] bg-[#1e2a18] p-3 text-sm text-leaf', {},
        s.kycEnabled
          ? 'Cash-out is OPEN. A player must be KYC-approved before a request is accepted.'
          : 'Cash-out is OPEN, and identity verification is NOT required.'));
      return;
    }
    banner.appendChild(el('div',
      'game-font rounded-lg border-2 border-[#8a6a45] bg-[#2a2118] p-3 text-sm text-gold', {},
      'Cash-out is CLOSED — players cannot file a withdrawal. Turn it on under Settings → Economy.'));
  }

  async function loadBanner() {
    try {
      const s = await adminApi.settings();
      if (!cancelled) renderBanner(s);
    } catch { /* the queues below still work; a missing banner is not an error */ }
  }

  // ─── Withdrawals ───────────────────────────────────────────────────────────
  async function loadWithdrawals() {
    clear(withdrawalsHost);
    withdrawalsHost.appendChild(spinner());
    try {
      const res = await adminListWithdrawals() as { items: CoreAdminWithdrawal[] };
      if (cancelled) return;
      clear(withdrawalsHost);
      withdrawalsHost.appendChild(withdrawalTable(res.items));
    } catch (e) {
      if (cancelled) return;
      clear(withdrawalsHost);
      withdrawalsHost.appendChild(
        errorBox(apiMessage(e, "Couldn't load withdrawal requests."), () => void loadWithdrawals()));
    }
  }

  function withdrawalTable(items: CoreAdminWithdrawal[]): HTMLElement {
    if (!items.length) return emptyBox('No withdrawal requests.');
    return table(
      ['Requested', 'Player', 'Gross', 'Fee', 'Net payout', 'Method', 'Status', ''],
      items.map((w) => [
        fmtDateTime(w.createdAt),
        el('span', 'font-mono text-xs', { title: w.userId },
          w.user?.username ?? w.user?.email ?? w.userId.slice(0, 8)),
        eur(w.amountCents),
        eur(w.feeCents),
        eur(w.netCents),
        w.payoutMethod ?? '—',
        pill(w.status, STATUS_TONE[w.status] ?? 'mute'),
        w.status === 'PENDING' ? actions(w) : el('span', 'text-text-dim text-xs', {},
          w.invoiceNumber ? w.invoiceNumber : (w.reason ?? '')),
      ]));
  }

  function actions(w: CoreAdminWithdrawal): HTMLElement {
    return el('div', 'flex gap-2', {},
      btn('Approve', () => void approve(w), 'primary'),
      btn('Reject', () => void reject(w), 'danger'));
  }

  async function approve(w: CoreAdminWithdrawal) {
    const ok = await confirmDialog(
      'Approve withdrawal',
      `Pay out ${eur(w.netCents)} (gross ${eur(w.amountCents)}, fee ${eur(w.feeCents)})? `
      + 'The payout is sent to the gateway; this records it as approved.',
      'Approve');
    if (!ok) return;
    try {
      await adminApproveWithdrawal(w.id);
      toast('Withdrawal approved');
      void loadWithdrawals();
    } catch (e) { toastError(e, 'Could not approve the withdrawal'); }
  }

  async function reject(w: CoreAdminWithdrawal) {
    const reason = input('Why is this being rejected?');
    const close = modal('Reject withdrawal',
      el('div', 'flex flex-col gap-3', {},
        el('div', 'text-sm text-text-dim', {},
          `${eur(w.amountCents)} goes straight back to the player's balance.`),
        labelled('Reason', reason)),
      [
        btn('Cancel', () => close()),
        btn('Reject', async () => {
          close();
          try {
            await adminRejectWithdrawal(w.id, reason.value.trim() || 'Rejected');
            toast('Withdrawal rejected — balance refunded');
            void loadWithdrawals();
          } catch (e) { toastError(e, 'Could not reject the withdrawal'); }
        }, 'danger'),
      ]);
  }

  // ─── KYC ───────────────────────────────────────────────────────────────────
  async function loadKyc() {
    clear(kycHost);
    kycHost.appendChild(spinner());
    try {
      const page = await adminApi.kycUsers();
      if (cancelled) return;
      clear(kycHost);
      kycHost.appendChild(kycTable(page.items));
    } catch (e) {
      if (cancelled) return;
      clear(kycHost);
      kycHost.appendChild(
        errorBox(apiMessage(e, "Couldn't load the verification queue."), () => void loadKyc()));
    }
  }

  function kycTable(items: AdminKycUser[]): HTMLElement {
    if (!items.length) return emptyBox('Nobody has submitted documents yet.');
    return table(
      ['Submitted', 'Player', 'Name given', 'Country', 'Status', ''],
      items.map((u) => [
        fmtDateTime(u.kycSubmittedAt),
        el('div', 'min-w-0', {},
          el('div', 'text-sm truncate', {}, u.username ?? u.email),
          el('div', 'text-[11px] text-text-dim truncate', {}, u.email)),
        u.kycFullName ?? '—',
        u.kycCountry ?? '—',
        pill(u.kycStatus, STATUS_TONE[u.kycStatus] ?? 'mute'),
        u.kycStatus === 'PENDING'
          ? el('div', 'flex gap-2', {},
            btn('Approve', () => void approveKyc(u), 'primary'),
            btn('Reject', () => void rejectKyc(u), 'danger'))
          : el('span', 'text-text-dim text-xs', {}, fmtDateTime(u.kycReviewedAt)),
      ]));
  }

  async function approveKyc(u: AdminKycUser) {
    try {
      await adminApi.approveKyc(u.id);
      toast('Identity approved');
      void loadKyc();
    } catch (e) { toastError(e, 'Could not approve this player'); }
  }

  async function rejectKyc(u: AdminKycUser) {
    const reason = input('Why are the documents being rejected?');
    const close = modal('Reject verification',
      el('div', 'flex flex-col gap-3', {},
        el('div', 'text-sm text-text-dim', {},
          'The player is told the reason and may submit again.'),
        labelled('Reason', reason)),
      [
        btn('Cancel', () => close()),
        btn('Reject', async () => {
          const text = reason.value.trim();
          // The core endpoint requires one — an unexplained rejection leaves the
          // player with nothing to fix.
          if (!text) { toast('Give a reason for the rejection', 'error'); return; }
          close();
          try {
            await adminApi.rejectKyc(u.id, text);
            toast('Verification rejected');
            void loadKyc();
          } catch (e) { toastError(e, 'Could not reject this player'); }
        }, 'danger'),
      ]);
  }

  void loadBanner();
  void loadWithdrawals();
  void loadKyc();

  return () => { cancelled = true; };
}
